/**
 * =============================================================================
 * Componente: AffiliateBrandingTab
 * Versão: 1.0.0
 *
 * Descrição:
 *   Aba "Indicação" da tela de Configurações do tenant. Permite ao admin
 *   personalizar a identidade visual do Painel do Afiliado da empresa:
 *     - Upload da logo (bucket "affiliate", pasta branding/<tenant_id>/)
 *     - Cor primária e cor secundária (HSL) que compõem o gradiente
 *     - Textos da tela de login (título, subtítulo, bullets, rodapé)
 *
 *   Persiste tudo via RPC `update_affiliate_program_branding`, que faz
 *   merge no `affiliate_programs.settings`. Sem mudanças de schema.
 * =============================================================================
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Upload, Trash2, Image as ImageIcon, Plus, X, Copy, Check, Link2, UserPlus, AlertTriangle } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { useDefaultAffiliateProgram } from '@/hooks/useDefaultAffiliateProgram';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import {
  useAffiliateBranding,
  type AffiliateBrandingLoginBullet,
} from '@/hooks/useAffiliateBranding';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { BrandingPreview } from './BrandingPreview';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { createLogger } from '@/lib/logger';

const log = createLogger('AffiliateBrandingTab');

// Defaults (valores atuais do tema indigo/violeta do affiliate-shell)
const DEFAULTS = {
  primary_hsl: '258 81% 25%',
  accent_hsl: '173 77% 57%',
  login: {
    title: 'Indique. Acompanhe. Receba.',
    subtitle: 'Painel premium para afiliados acompanharem indicações, comissões e saques em tempo real.',
    footer_text: 'Ao entrar, você concorda com os termos do programa de indicação.',
    bullets: [
      { icon: 'link' as const,   text: 'Links únicos por campanha, prontos para divulgar' },
      { icon: 'chart' as const,  text: 'Funil de indicações em tempo real' },
      { icon: 'wallet' as const, text: 'Saques rápidos via PIX' },
    ],
  },
};

const BULLET_ICONS = [
  { value: 'link',     label: 'Link / Cursor' },
  { value: 'chart',    label: 'Gráfico' },
  { value: 'wallet',   label: 'Carteira' },
  { value: 'check',    label: 'Check' },
  { value: 'sparkles', label: 'Sparkles' },
];

// Conversão simples HEX → HSL "H S% L%"
function hexToHsl(hex: string): string | null {
  const m = hex.replace('#', '').match(/^([\da-f]{6})$/i);
  if (!m) return null;
  const num = parseInt(m[1], 16);
  const r = ((num >> 16) & 255) / 255;
  const g = ((num >> 8) & 255) / 255;
  const b = (num & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function hslToHex(hsl: string | null | undefined): string {
  if (!hsl) return '#7c3aed';
  const m = hsl.match(/^(\d+)\s+(\d+)%\s+(\d+)%$/);
  if (!m) return '#7c3aed';
  const h = parseInt(m[1], 10) / 360;
  const s = parseInt(m[2], 10) / 100;
  const l = parseInt(m[3], 10) / 100;
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  const toHex = (v: number) => Math.round(v * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function AffiliateBrandingTab() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;
  const tenantSlug = currentTenant?.slug ?? null;
  const { data: existing, isLoading } = useAffiliateBranding(tenantId);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Feedback visual do botão "copiar URL".
  const [copied, setCopied] = useState(false);
  const [copiedSignup, setCopiedSignup] = useState(false);

  // URL canônica de login do afiliado deste tenant (compartilhável).
  // Quando o tenant ainda não tem slug, mostramos o fallback global.
  const loginUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    const origin = window.location.origin;
    return tenantSlug
      ? `${origin}/afiliado/${tenantSlug}/login`
      : `${origin}/afiliado/login`;
  }, [tenantSlug]);

  // URL pública de CADASTRO de novos afiliados — resolve o programa padrão
  // do tenant via /afiliado/<slug>/cadastro.
  const signupUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    const origin = window.location.origin;
    return tenantSlug
      ? `${origin}/afiliado/${tenantSlug}/cadastro`
      : `${origin}/afiliado/cadastro`;
  }, [tenantSlug]);

  // Resolve o programa padrão para alertar o admin caso ele ainda não exista.
  const { data: defaultProgram, isLoading: loadingDefault } =
    useDefaultAffiliateProgram(tenantSlug);

  async function handleCopyUrl() {
    try {
      await navigator.clipboard.writeText(loginUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast({ title: 'Não foi possível copiar', variant: 'destructive' });
    }
  }

  async function handleCopySignupUrl() {
    try {
      await navigator.clipboard.writeText(signupUrl);
      setCopiedSignup(true);
      setTimeout(() => setCopiedSignup(false), 1800);
    } catch {
      toast({ title: 'Não foi possível copiar', variant: 'destructive' });
    }
  }

  // Estado local controlado
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [primaryHsl, setPrimaryHsl] = useState<string>(DEFAULTS.primary_hsl);
  const [accentHsl, setAccentHsl] = useState<string>(DEFAULTS.accent_hsl);
  const [title, setTitle] = useState<string>(DEFAULTS.login.title);
  const [subtitle, setSubtitle] = useState<string>(DEFAULTS.login.subtitle);
  const [footerText, setFooterText] = useState<string>(DEFAULTS.login.footer_text);
  const [bullets, setBullets] = useState<AffiliateBrandingLoginBullet[]>(DEFAULTS.login.bullets);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Hidrata estado quando carrega
  useEffect(() => {
    if (!existing) return;
    if (existing.branding) {
      setLogoUrl(existing.branding.logo_url ?? null);
      setPrimaryHsl(existing.branding.primary_hsl ?? DEFAULTS.primary_hsl);
      setAccentHsl(existing.branding.accent_hsl ?? DEFAULTS.accent_hsl);
    }
    if (existing.login) {
      setTitle(existing.login.title ?? DEFAULTS.login.title);
      setSubtitle(existing.login.subtitle ?? DEFAULTS.login.subtitle);
      setFooterText(existing.login.footer_text ?? DEFAULTS.login.footer_text);
      if (existing.login.bullets?.length) setBullets(existing.login.bullets);
    }
  }, [existing]);

  const previewBranding = useMemo(
    () => ({ logo_url: logoUrl, primary_hsl: primaryHsl, accent_hsl: accentHsl }),
    [logoUrl, primaryHsl, accentHsl],
  );

  // Upload da logo
  async function handleLogoUpload(file: File) {
    if (!tenantId) return;
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: 'Arquivo muito grande', description: 'Máximo 2MB.', variant: 'destructive' });
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'].includes(file.type)) {
      toast({ title: 'Formato inválido', description: 'Use PNG, JPG, WEBP ou SVG.', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split('.').pop() ?? 'png';
      const path = `branding/${tenantId}/logo-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('affiliate')
        .upload(path, file, { upsert: true, contentType: file.type, cacheControl: '3600' });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from('affiliate').getPublicUrl(path);
      setLogoUrl(pub.publicUrl);
      toast({ title: 'Logo enviada', description: 'Lembre-se de salvar para aplicar.' });
    } catch (err: any) {
      log.error('Erro no upload de logo', err);
      toast({ title: 'Erro no upload', description: err?.message ?? 'Falha ao enviar.', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  }

  // Persistência
  async function handleSave() {
    if (!tenantId) return;
    setSaving(true);
    try {
      const { error } = await supabase.rpc('update_affiliate_program_branding', {
        p_branding: {
          logo_url: logoUrl,
          primary_hsl: primaryHsl,
          accent_hsl: accentHsl,
        },
        p_login: {
          title: title.trim(),
          subtitle: subtitle.trim(),
          footer_text: footerText.trim(),
          bullets: bullets.filter((b) => b.text.trim().length > 0).slice(0, 4),
        },
        p_tenant_hint: tenantId,
      } as any);
      if (error) throw error;
      toast({ title: 'Salvo!', description: 'A identidade visual do painel foi atualizada.' });
      queryClient.invalidateQueries({ queryKey: ['affiliate-branding'] });
      queryClient.invalidateQueries({ queryKey: ['affiliate-branding-public'] });
    } catch (err: any) {
      log.error('Erro ao salvar branding', err);
      toast({ title: 'Erro ao salvar', description: err?.message ?? 'Tente novamente.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
      {/* Coluna de configuração */}
      <div className="space-y-6">
        {/* v1.2.0 — Banner neutro alinhado ao padrão SaaS das demais abas */}
        <div className="flex items-start gap-3 rounded-lg border border-border/60 bg-muted/30 p-4 text-sm">
          <ImageIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="space-y-0.5">
            <p className="font-medium text-foreground">
              Fonte única de personalização visual
            </p>
            <p className="text-muted-foreground">
              Esta é a única tela onde a logo e as cores do painel/landing pública
              do afiliado são configuradas. Os ajustes valem para todos os
              programas de indicação deste tenant.
            </p>
          </div>
        </div>

        {/* URL canônica do tenant — link público que o admin entrega aos afiliados */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Link2 className="h-4 w-4 text-muted-foreground" />
              URL do painel do afiliado
            </CardTitle>
            <CardDescription>
              {tenantSlug
                ? 'Compartilhe este link com seus afiliados. Ele exibe o branding personalizado da sua empresa na tela de login.'
                : 'Sua empresa ainda não tem um identificador (slug) cadastrado. Por enquanto será usado o link global.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-stretch gap-2">
              <Input
                readOnly
                value={loginUrl}
                onFocus={(e) => e.currentTarget.select()}
                className="font-mono text-xs sm:text-sm"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleCopyUrl}
                className="shrink-0 gap-1.5"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4" />
                    Copiado
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copiar
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* URL pública de cadastro de novos afiliados — usa o programa padrão */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-muted-foreground" />
              URL pública de cadastro de afiliados
            </CardTitle>
            <CardDescription>
              Compartilhe este link para que novos afiliados se cadastrem direto
              no programa de indicação <strong>padrão</strong> da sua empresa.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-stretch gap-2">
              <Input
                readOnly
                value={signupUrl}
                onFocus={(e) => e.currentTarget.select()}
                className="font-mono text-xs sm:text-sm"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleCopySignupUrl}
                className="shrink-0 gap-1.5"
                disabled={!defaultProgram && !loadingDefault}
              >
                {copiedSignup ? (
                  <>
                    <Check className="h-4 w-4" />
                    Copiado
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copiar
                  </>
                )}
              </Button>
            </div>

            {/* Estado: tenant ainda não definiu programa padrão.
                Visual neutro (border + muted) para alinhar com padrão SaaS,
                evitando excesso de cor de alerta. */}
            {!loadingDefault && !defaultProgram && (
              <div className="flex items-start gap-2.5 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-foreground/60" />
                <div className="space-y-1.5">
                  <p className="text-foreground/80">
                    Nenhum programa de indicação está marcado como{' '}
                    <span className="font-medium text-foreground">padrão</span>.
                    O link acima só ficará ativo após essa definição.
                  </p>
                  <RouterLink
                    to="/tenant/indicacao/programas"
                    className="inline-flex items-center gap-1 font-medium text-foreground hover:underline underline-offset-2"
                  >
                    Definir programa padrão →
                  </RouterLink>
                </div>
              </div>
            )}

            {/* Estado: tudo certo, mostra o programa que será usado */}
            {defaultProgram && (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Programa padrão atual:{' '}
                <strong className="text-foreground font-medium">
                  {defaultProgram.program_name}
                </strong>
              </p>
            )}
          </CardContent>
        </Card>

        {/* Identidade visual */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Identidade visual</CardTitle>
            <CardDescription>
              Logo do painel do afiliado (opcional — sobrescreve a logo da empresa) e cores do gradiente.
              {' '}
              <RouterLink to="/tenant/company" className="text-primary hover:underline font-medium">
                Editar logo padrão em Empresa →
              </RouterLink>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <Label className="text-sm">Logo do painel do afiliado (opcional)</Label>
              <div className="mt-2 flex items-center gap-4">
                <div className="flex h-20 w-20 items-center justify-center rounded-xl border border-dashed bg-muted/40 overflow-hidden">
                  {logoUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={logoUrl} alt="Logo" className="max-h-full max-w-full object-contain" />
                  ) : (
                    <ImageIcon className="h-7 w-7 text-muted-foreground" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleLogoUpload(f);
                      e.target.value = '';
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                    {logoUrl ? 'Trocar logo' : 'Enviar logo'}
                  </Button>
                  {logoUrl && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => setLogoUrl(null)}>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Remover
                    </Button>
                  )}
                  <p className="text-xs text-muted-foreground">
                    PNG, JPG, WEBP ou SVG · até 2MB
                    <br />
                    Se deixar vazio, será usada a logomarca cadastrada em <strong>Empresa</strong>.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Cor primária</Label>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="color"
                    value={hslToHex(primaryHsl)}
                    onChange={(e) => {
                      const hsl = hexToHsl(e.target.value);
                      if (hsl) setPrimaryHsl(hsl);
                    }}
                    className="h-10 w-14 cursor-pointer rounded-md border border-border bg-background p-1"
                  />
                  <Input
                    value={primaryHsl}
                    onChange={(e) => setPrimaryHsl(e.target.value)}
                    placeholder="258 81% 25%"
                    className="font-mono text-xs"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm">Cor secundária</Label>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="color"
                    value={hslToHex(accentHsl)}
                    onChange={(e) => {
                      const hsl = hexToHsl(e.target.value);
                      if (hsl) setAccentHsl(hsl);
                    }}
                    className="h-10 w-14 cursor-pointer rounded-md border border-border bg-background p-1"
                  />
                  <Input
                    value={accentHsl}
                    onChange={(e) => setAccentHsl(e.target.value)}
                    placeholder="173 77% 57%"
                    className="font-mono text-xs"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tela de login */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Tela de login</CardTitle>
            <CardDescription>Personalize os textos exibidos para o afiliado ao entrar.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm">Título</Label>
              <Input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} className="mt-2" />
            </div>
            <div>
              <Label className="text-sm">Subtítulo / descrição</Label>
              <Textarea value={subtitle} maxLength={600} rows={3} onChange={(e) => setSubtitle(e.target.value)} className="mt-2" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-sm">Bullets (até 4)</Label>
                {bullets.length < 4 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setBullets((b) => [...b, { icon: 'sparkles', text: '' }])}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    Adicionar
                  </Button>
                )}
              </div>
              <div className="mt-2 space-y-2">
                {bullets.map((b, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Select
                      value={b.icon}
                      onValueChange={(v) =>
                        setBullets((arr) => arr.map((x, idx) => (idx === i ? { ...x, icon: v as any } : x)))
                      }
                    >
                      <SelectTrigger className="w-[140px] shrink-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BULLET_ICONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={b.text}
                      maxLength={200}
                      onChange={(e) =>
                        setBullets((arr) => arr.map((x, idx) => (idx === i ? { ...x, text: e.target.value } : x)))
                      }
                      placeholder="Texto do bullet"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setBullets((arr) => arr.filter((_, idx) => idx !== i))}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm">Texto de rodapé</Label>
              <Input value={footerText} maxLength={400} onChange={(e) => setFooterText(e.target.value)} className="mt-2" />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} size="lg">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Salvar configurações
          </Button>
        </div>
      </div>

      {/* Coluna de preview */}
      <div className="lg:sticky lg:top-6 lg:self-start">
        <BrandingPreview
          branding={previewBranding}
          login={{
            title,
            subtitle,
            footer_text: footerText,
            bullets,
          }}
        />
      </div>
    </div>
  );
}
